<template>
  <!-- 移动端筛选抽屉：从左侧滑出，内部按视图栈切换（根视图 / 分类下钻 / 单项筛选） -->
  <div :class="['mobile-filter-drawer', visible ? 'open' : '']">
    <div class="drawer-mask" @click="close"></div>
    <div class="drawer-panel">
      <!-- 头部：返回 + 关闭 -->
      <div class="drawer-header">
        <a class="drawer-back" @click="goBack">
          <i class="css-arrow arrow-left"></i>
          <span>{{ backText }}</span>
        </a>
        <a class="drawer-close" @click="close">
          <i class="css-close"></i>
        </a>
      </div>
      <!-- 主体：随 view 切换 -->
      <div class="drawer-body">
        <!-- 根视图：筛选项入口列表 -->
        <template v-if="view.type === 'root'">
          <div class="drawer-section-label">筛选</div>
          <ul class="nav-list">
            <li v-for="f in availableFilters" :key="f.key" @click="openFilter(f.key)">
              <span>{{ f.label }}</span>
              <i class="css-arrow arrow-right"></i>
            </li>
          </ul>
        </template>
        <!-- 分类一级：分组可下钻，叶子直接勾选 -->
        <template v-else-if="view.type === 'category'">
          <div class="drawer-section-label">分类</div>
          <ul class="nav-list">
            <template v-for="row in categoryRootRows">
              <li v-if="row.isGroup" :key="'g-' + row.node.id" @click="drillCategory(row.node)">
                <span>{{ row.node.name }}</span>
                <i class="css-arrow arrow-right"></i>
              </li>
              <li v-else :key="'l-' + row.node.id" class="check-row" @click="toggleItem('category', row.node.id)">
                <i class="iconfont">{{ isItemChecked('category', row.node.id) ? '\uedaf' : '\uec58' }}</i>
                <span>{{ row.node.name }}</span>
              </li>
            </template>
          </ul>
        </template>
        <!-- 分类下级：当前层标题行（可收起） + 子树手风琴（分组展开/收起，叶子勾选） -->
        <template v-else-if="view.type === 'categoryNode' && currentNode">
          <div class="cat-current" @click="catCurrentOpen = !catCurrentOpen">
            <span>{{ currentNode.name }}</span>
            <i :class="['css-arrow', catCurrentOpen ? 'arrow-up' : 'arrow-down']"></i>
          </div>
          <ul v-if="catCurrentOpen" class="cat-rows">
            <template v-for="row in categoryNodeRows">
              <li v-if="row.isGroup" :key="'g-' + row.node.id"
                :class="['cat-group', 'depth-' + row.depth, row.blockEnd ? 'block-end' : '']"
                @click="toggleCatGroup(row.node)">
                <span>{{ row.node.name }}</span>
                <i :class="['css-arrow', row.node._collapse ? 'arrow-down' : 'arrow-up']"></i>
              </li>
              <li v-else :key="'l-' + row.node.id"
                :class="['cat-leaf', 'depth-' + row.depth, row.blockEnd ? 'block-end' : '']"
                @click="toggleItem('category', row.node.id)">
                <i class="iconfont">{{ isItemChecked('category', row.node.id) ? '\uedaf' : '\uec58' }}</i>
                <span>{{ row.node.name }}</span>
              </li>
            </template>
          </ul>
        </template>
        <!-- 其余筛选：统一在第二层抽屉里展示 -->
        <template v-else-if="view.type === 'filter'">
          <div class="drawer-section-label">{{ filterLabel(view.key) }}</div>
          <!-- 系列：搜索 + 复选列表 -->
          <div v-if="view.key === 'series'" class="series-box">
            <div class="series-search">
              <i class="iconfont-ot-website search-icon">&#xe690;</i>
              <input type="text" v-model="seriesSearchKw" placeholder="搜索" />
              <i v-if="seriesSearchKw" class="iconfont-ot-website close left-close-icon"
                @click="seriesSearchKw = ''">&#xe6df;</i>
            </div>
            <ul class="check-list">
              <li v-for="item in filteredSeriesItems" :key="String(item.id)"
                @click="toggleItem('series', item.value || item.id)">
                <i class="iconfont">{{ isItemChecked('series', item.value || item.id) ? '\uedaf' : '\uec58' }}</i>
                <span>{{ item.name }}</span>
                <span v-if="item.count != null" class="row-count">（{{ item.count }}）</span>
              </li>
              <li v-if="!filteredSeriesItems.length" class="empty-row">无匹配项</li>
            </ul>
          </div>
          <!-- 颜色：色块网格（选中描边） -->
          <ul v-else-if="view.key === 'colors'" class="color-grid">
            <li v-for="(item, i) in filterOpts.colors.items" :key="i"
              :class="{ active: isItemChecked('colors', item.name) }" @click="toggleItem('colors', item.name)">
              <span class="swatch" :style="{ backgroundColor: item.value }">
                <img :src="`assets/ColorBlock/${item.value}.png` | ossPath" />
              </span>
              <span class="swatch-name">{{ item.name }}</span>
            </li>
          </ul>
          <!-- 尺码/尺寸：按钮网格 -->
          <ul v-else-if="view.key === 'sizes' || view.key === 'measurements'" class="size-grid">
            <li v-for="(item, i) in filterOpts[view.key].items" :key="i"
              :class="{ active: isItemChecked(view.key, itemRef(view.key, item)) }"
              @click="toggleItem(view.key, itemRef(view.key, item))">
              <a>{{ item.name }}</a>
            </li>
          </ul>
          <!-- 其余（品牌/性别/长度/袖长）：复选列表（品牌展示 logo） -->
          <ul v-else class="check-list">
            <li v-for="(item, i) in filterOpts[view.key].items" :key="i"
              :class="view.key === 'brand' ? 'brand-item' : ''" @click="toggleItem(view.key, itemRef(view.key, item))">
              <i class="iconfont">{{ isItemChecked(view.key, itemRef(view.key, item)) ? '\uedaf' : '\uec58' }}</i>
              <img v-if="view.key === 'brand' && item.logo" :src="item.logo" :alt="item.name" class="brand-logo" />
              <span v-else>{{ item.name || item.value }}</span>
              <span v-if="item.count != null && view.key !== 'brand'" class="row-count">（{{ item.count }}）</span>
            </li>
          </ul>
        </template>
      </div>
      <!-- 底部：应用并关闭（勾选即时生效，与 PC 一致） -->
      <div class="drawer-footer" @click="close">
        <span>显示 {{ total }} 项</span>
        <i class="css-arrow arrow-right white"></i>
      </div>
    </div>
  </div>
</template>

<script>
import filterOptionMixin from '@/mixins/filterOption';

export default {
  name: 'MobileFilterDrawer',
  mixins: [filterOptionMixin],
  props: {
    // 抽屉显示状态（父组件控制）
    visible: Boolean,
    filterOpts: Object,
    // 当前商品总数，用于底部"显示 X 项"
    total: Number
  },
  data() {
    return {
      // 视图栈当前状态：root(根) / category(分类一级) / categoryNode(分类下钻) / filter(单项筛选)
      view: { type: 'root' },
      // 分类下钻视图：当前层标题行默认展开
      catCurrentOpen: true
    };
  },
  computed: {
    // 头部返回文案：根/下钻层显示"返回"，其余显示当前筛选名
    backText() {
      if (this.view.type === 'root' || this.view.type === 'categoryNode') return '返回';
      if (this.view.type === 'category') return '分类';
      return this.filterLabel(this.view.key);
    },
    // 可用筛选项（按设计稿顺序，仅展示有数据的项）
    availableFilters() {
      const defs = [
        { key: 'category', label: '分类' },
        { key: 'series', label: '系列' },
        { key: 'colors', label: '颜色' },
        { key: 'brand', label: '品牌' },
        { key: 'genders', label: '性别' },
        { key: 'sizes', label: '尺码' },
        { key: 'clothingLength', label: '长度' },
        { key: 'sleeveLength', label: '袖长' },
        { key: 'measurements', label: '尺寸' }
      ];
      return defs.filter(d => this.filterOpts[d.key]?.items?.length);
    },
    // 分类下钻视图当前节点（按 id 从树中查找）
    currentNode() {
      return this.view.type === 'categoryNode'
        ? this.findCategoryNode(this.view.id, this.filterOpts.category.items)
        : null;
    },
    // 分类一级：根据 _collapse 拉平成行（带 depth），供模板循环渲染
    categoryRootRows() {
      return this.flattenCategory(this.filterOpts.category.items);
    },
    // 分类下钻视图：当前节点子树拉平成行
    categoryNodeRows() {
      return this.currentNode ? this.flattenCategory(this.currentNode.children) : [];
    }
  },
  watch: {
    visible(val) {
      // 打开时回到根视图并清空系列搜索，同时锁定背景滚动
      if (val) {
        this.view = { type: 'root' };
        this.seriesSearchKw = '';
      }
      document.body.style.overflow = val ? 'hidden' : '';
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    close() {
      this.$emit('close');
    },
    // 头部返回：根视图关闭抽屉，下钻层逐级回退
    goBack() {
      if (this.view.type === 'root') {
        this.close();
      } else if (this.view.type === 'categoryNode') {
        this.view = { type: 'category' };
      } else {
        this.view = { type: 'root' };
      }
    },
    filterLabel(key) {
      const def = this.availableFilters.find(d => d.key === key);
      return def ? def.label : key;
    },
    openFilter(key) {
      // 分类进入独立的一级列表，其余直接展示第二层选项
      if (key === 'category') {
        this.view = { type: 'category' };
      } else {
        this.view = { type: 'filter', key };
      }
    },
    drillCategory(node) {
      // 进入分类下钻视图，默认展开当前层内容
      this.view = { type: 'categoryNode', id: node.id };
      this.catCurrentOpen = true;
    },
    // 分类分组节点展开/收起（_collapse 在 fillFilters 赋值前已初始化，是响应式属性）
    toggleCatGroup(node) {
      if (node && node.children && node.children.length) {
        node._collapse = !node._collapse;
      }
    },
    // 把分类树按展开状态拉平成行：isGroup 标记分组行，depth 用于缩进，
    // blockEnd 标记 depth-0 分组块的收尾行（用于补块尾分割线，最后一个块除外）
    flattenCategory(nodes) {
      const rows = [];
      const walk = (list, depth) => (list || []).forEach(n => {
        const isGroup = !!(n.children && n.children.length);
        rows.push({ node: n, depth, isGroup });
        if (isGroup && !n._collapse) walk(n.children, depth + 1);
      });
      walk(nodes, 0);
      // 逐块回标：每个 depth-0 分组块的最后一条可见行打 blockEnd（最后一块不标）
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].depth === 0 && rows[i].isGroup) {
          let j = i + 1;
          while (j < rows.length && rows[j].depth > 0) j++;
          if (j < rows.length) rows[j - 1].blockEnd = true;
        }
      }
      return rows;
    },
    // 按 id 在分类树中递归查找节点
    findCategoryNode(id, items) {
      for (const n of items || []) {
        if (String(n.id) === String(id)) return n;
        if (n.children && n.children.length) {
          const found = this.findCategoryNode(id, n.children);
          if (found) return found;
        }
      }
      return null;
    },
    // 各筛选项勾选值取值口径与 PC 保持一致：brand/genders/sizes/clothingLength/sleeveLength/measurements 传 id
    // （series 在模板里单独传 value）
    itemRef(key, item) {
      if (key === 'brand' || key === 'genders' || key === 'sizes' || key === 'clothingLength' || key === 'sleeveLength' || key === 'measurements') {
        return item.id;
      }
      return item.name || item.value;
    },
    // 勾选状态：兼容多选数组与单选字符串（价格）
    isItemChecked(key, ref) {
      const checked = this.filterOpts[key]?.checked;
      return Array.isArray(checked) ? checked.indexOf(String(ref)) != -1 : checked == String(ref);
    }
  }
};
</script>

<style lang="scss" scoped>
.mobile-filter-drawer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1200;
  pointer-events: none;

  .drawer-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity 0.25s;
  }

  .drawer-panel {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100%;
    background: #fff;
    display: flex;
    flex-direction: column;
    transform: translateX(-100%);
    transition: transform 0.25s;
  }

  &.open {
    pointer-events: auto;

    .drawer-mask {
      opacity: 1;
    }

    .drawer-panel {
      transform: none;
    }
  }

  .drawer-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 15px;

    .drawer-back {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #000;
      text-decoration: none;
      cursor: pointer;
      padding-left: 2px;

      .css-arrow {
        margin-right: 10px;
      }
    }

    .drawer-close {
      display: block;
      padding: 6px;
      cursor: pointer;
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 0 15px;
  }

  .drawer-section-label {
    font-size: 12px;
    color: #000;
    padding: 6px 0 5px;
    text-align: left;
  }

  // 纯 CSS 箭头（避免依赖未知 iconfont 字形）
  .css-arrow {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-style: solid;
    border-color: #000;
    border-width: 0;
    flex-shrink: 0;

    &.arrow-left {
      border-width: 0 0 1.5px 1.5px;
      transform: rotate(45deg);
    }

    &.arrow-right {
      border-width: 1.5px 1.5px 0 0;
      transform: rotate(45deg);
    }

    &.arrow-down {
      border-width: 0 1.5px 1.5px 0;
      transform: rotate(45deg);
    }

    &.arrow-up {
      border-width: 1.5px 0 0 1.5px;
      transform: rotate(45deg);
    }

    &.white {
      border-color: #fff;
    }
  }

  // 关闭 X（两根旋转的横线）
  .css-close {
    position: relative;
    display: block;
    width: 14px;
    height: 14px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 1.5px;
      background: #000;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }
  }

  // 根视图 / 分类一级：入口行列表
  .nav-list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      color: #000;
      line-height: 1;
      padding: 15px 0;
      border-bottom: 1px solid #BABEC2;
      cursor: pointer;

      // 分类一级里的叶子行：checkbox + 名称左对齐
      &.check-row {
        justify-content: flex-start;

        .iconfont {
          font-size: 16px;
          margin-right: 10px;
        }
      }
    }
  }

  // 复选行列表（系列/品牌/性别/长度/袖长等）
  .check-list {
    margin: 0;
    padding: 4px 0 20px;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #000;
      padding: 15px 0;
      cursor: pointer;

      .iconfont {
        font-size: 16px;
        margin-right: 10px;
        flex-shrink: 0;
      }

      .row-count {
        font-size: 12px;
        color: #999;
      }

      // 品牌项下方 1px 浅灰分隔线(仅品牌列表, 其他文字分组无线)
      &.brand-item {
        border-bottom: 1px solid #E7E8EA;
        height: 36px;

      }

      .brand-logo {
        // 高度 12px~36px 区间自适应
        height: auto;
        min-height: 12px;
        max-height: 36px;
        width: auto;
        object-fit: cover;
        display: block;
      }
    }

    .empty-row {
      justify-content: center;
      color: #999;
      font-size: 12px;
      cursor: default;
    }
  }

  // 系列：搜索框 + 列表
  .series-box {
    padding-bottom: 20px;

    // 系列列表限高滚动，避免整页过长
    .check-list {
      max-height: 64vh;
      overflow-y: auto;
    }

    .series-search {
      display: flex;
      align-items: center;
      height: 36px;
      border: 1px solid #BABEC2;
      padding: 0 10px;
      margin: 4px 0 10px;

      .search-icon {
        font-size: 20px;
        margin-right: 8px;
        color: #BABEC2;
      }

      input {
        flex: 1;
        border: none;
        outline: none;
        height: 100%;
        font-size: 14px;
        background: transparent;
        color: #000;
        padding: 0;

        &::placeholder {
          color: #999;
        }
      }

      .series-clear {
        font-size: 18px;
        line-height: 1;
        color: #BABEC2;
        padding: 0 2px;
        text-decoration: none;
      }
    }
  }

  // 颜色：色块网格
  .color-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -5px;
    padding: 4px 0 20px;
    list-style: none;

    li {
      box-sizing: border-box;
      width: calc(25% - 10px);
      margin: 5px;
      // 白底 + 浅灰描边(设计稿), 选中换黑框
      border: 1px solid #DDD;
      background: #fff;
      padding: 12px 4px 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;

      &.active {
        border-color: #000;
      }

      .swatch {
        position: relative;
        display: block;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        overflow: hidden;

        img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
      }

      .swatch-name {
        font-size: 12px;
        color: #000;
        margin-top: 8px;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  // 尺码/尺寸：按钮网格
  .size-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -5px;
    padding: 4px 0 20px;
    list-style: none;

    li {
      box-sizing: border-box;
      width: calc(25% - 10px);
      margin: 5px;
      border: 1px solid #ccc;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      a {
        font-size: 13px;
        color: #000;
        text-decoration: none;
      }

      &.active {
        border: 2px solid #000;
      }
    }
  }

  // 分类下钻视图
  .cat-current {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #000;
    font-weight: 700;
    line-height: 44px;
    border-bottom: 1px solid #BABEC2;
    cursor: pointer;
  }

  .cat-rows {
    margin: 0;
    padding: 4px 0 20px 15px;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      color: #000;
      line-height: 44px;
      cursor: pointer;

      .iconfont {
        font-size: 16px;
        margin-right: 10px;
        flex-shrink: 0;
      }

      &.cat-group {
        font-weight: 700;
        // 分组标题行下方分割线
        border-bottom: 1px solid #BABEC2;
      }

      // depth-0 分组块收尾分割线（最后一个块不标）
      &.block-end {
        // border-bottom: 1px solid #BABEC2;
      }

      // 分组缩进：depth-0/1/2/3 → 0/16/32/48px
      &.depth-1 {
        padding-left: 16px;
      }

      &.depth-2 {
        padding-left: 32px;
      }

      &.depth-3 {
        padding-left: 48px;
      }

      // 叶子行：checkbox + 名称左对齐
      &.cat-leaf {
        justify-content: flex-start;
      }
    }
  }

  // 底部应用栏
  .drawer-footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 15px;
    background: #000;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
  }
}

// PC 端不渲染抽屉（仅移动端布局使用，避免样式冲突）
@media only screen and (min-width: 1025px) {
  .mobile-filter-drawer {
    display: none;
  }
}
</style>
